import { SentryLogger } from "@/helpers/sentryLogger"
import { isNullish } from "@/helpers/typeguards"
import { CallDurationData, callDurationDataSchema } from "@/helpers/zodSchemas"
import { contactStorageService } from "@/services/localStorageService"

class CallDurationPersistenceService {
  private static instance: CallDurationPersistenceService

  private readonly sentryLogger = new SentryLogger()
  private readonly CALL_DURATION_KEY =
    contactStorageService.getCallDurationKey()

  public static getInstance(): CallDurationPersistenceService {
    if (!CallDurationPersistenceService.instance) {
      CallDurationPersistenceService.instance =
        new CallDurationPersistenceService()
    }

    return CallDurationPersistenceService.instance
  }

  private getStoredData(): CallDurationData | null {
    try {
      const rawData = contactStorageService.getItem(this.CALL_DURATION_KEY)
      if (isNullish(rawData)) {
        return null
      }
      const parsedData = callDurationDataSchema.safeParse(rawData)
      if (parsedData.success) {
        return parsedData.data
      }
      this.sentryLogger.warn("Invalid call duration data in localStorage", {
        rawData,
      })
      contactStorageService.removeItem(this.CALL_DURATION_KEY)

      return null
    } catch (error) {
      this.sentryLogger.error(error)

      return null
    }
  }

  private setStoredData(data: CallDurationData): void {
    try {
      contactStorageService.setItem(this.CALL_DURATION_KEY, data)
    } catch (error) {
      this.sentryLogger.error(error)
    }
  }

  public loadInitialCallDuration(currentContactId: string | null | undefined): {
    callContactId?: string
    callEndTimestamp: string | null
    callStartTimestamp: string | null
    wasSuccessfullyLoaded: boolean
  } {
    const storedData = this.getStoredData()

    if (!storedData) {
      return {
        callStartTimestamp: null,
        callEndTimestamp: null,
        wasSuccessfullyLoaded: false,
      }
    }

    if (currentContactId && storedData.contact_id !== currentContactId) {
      this.clearPersistedCallDuration()

      return {
        callStartTimestamp: null,
        callEndTimestamp: null,
        wasSuccessfullyLoaded: false,
      }
    }

    return {
      callStartTimestamp: storedData.call_start_timestamp,
      callEndTimestamp: storedData.call_end_timestamp,
      callContactId: storedData.contact_id,
      wasSuccessfullyLoaded: true,
    }
  }

  public saveCallStart(contactId: string, startTime: string): void {
    if (!contactId || !startTime) {
      this.sentryLogger.warn(
        "Attempted to save call start without contactId or startTime",
      )

      return
    }
    const data: CallDurationData = {
      contact_id: contactId,
      call_start_timestamp: startTime,
      call_end_timestamp: null,
    }
    this.setStoredData(data)
  }

  public saveCallEnd(
    contactId: string,
    startTime: string,
    endTime: string,
  ): void {
    if (!contactId || !startTime || !endTime) {
      this.sentryLogger.warn(
        "Attempted to save call end without contactId, startTime, or endTime",
      )

      return
    }

    const data: CallDurationData = {
      contact_id: contactId,
      call_start_timestamp: startTime,
      call_end_timestamp: endTime,
    }
    this.setStoredData(data)
  }

  public clearPersistedCallDuration(): void {
    try {
      contactStorageService.removeItem(this.CALL_DURATION_KEY)
    } catch (error) {
      this.sentryLogger.error(error)
    }
  }
}

const callDurationPersistenceService =
  CallDurationPersistenceService.getInstance()

export default callDurationPersistenceService
